/**
 * Plugin Content Pack — client-side entity executor (ADR-021; plan §10.2).
 *
 * Runs in the HOST page (platform origin), never in the plugin iframe. A
 * plugin's sandbox has no network, so the host reads the plugin's published
 * content pack on its behalf:
 *
 *  1. Credential — `host.fetchAccess(archeId)` (the platform's
 *     `GET /v1/arches/:id/content-access`) returns `{ baseUrl, manifestHash,
 *     signedQuery, expiresAt }`: a CloudFront signed policy covering ONLY
 *     that arche's prefix. `archeId` comes from the host's own context, so a
 *     plugin can only ever read its own pack. Cached per arche until close to
 *     expiry.
 *  2. Manifest — `<baseUrl>/m/<manifestHash>.json`, verified against its hash,
 *     cached in memory per hash.
 *  3. Objects — `<baseUrl>/o/<sha256>`, content-addressed and immutable: every
 *     body is hashed and must equal its manifest hash before it is used or
 *     cached, and a cached entry is re-verified before it is returned.
 *
 * Verbs ride in the input (`{ verb: 'list' | 'read' | 'url', … }`) — one entity,
 * N verbs, never a new bridge op.
 *
 * Expected failures are RESULTS, not throws: the bridge forwards only an
 * error's message, so `{ error: { code, message } }` (whole call) and per-path
 * `{ error }` entries are how typed codes reach the SDK (`useFiasContent`).
 *
 * The cache (plan §10.2): Cache Storage entries under a synthetic URL keyed by
 * environment + arche + hash, with IndexedDB metadata for least-recently-used
 * eviction inside a Web Lock, a budget of min(256 MB, 20 % of quota), and a
 * fallback to a 32 MB in-memory LRU wherever persistent storage is missing or
 * throws (private windows, blocked site data). A cache hit costs the
 * contributor nothing.
 *
 * Host services arrive through `configurePluginContentHost` at registration
 * (`client/src/lib/register-client-entities.ts`), because the executor context
 * carries only `{ userId, archeId }`.
 */

import type { ClientEntityDefinition, ClientEntityInvocationContext } from './types';

export const PLUGIN_CONTENT_ENTITY_ID = 'ent_intg_plugin_content_00000000';

/** Most paths one `read` / `url` call may name. */
export const PLUGIN_CONTENT_MAX_PATHS = 100;
/** Most bytes one `read` call returns across all its files. */
export const PLUGIN_CONTENT_MAX_READ_BYTES = 25 * 1024 * 1024;

/** A credential is refreshed when less than this remains. */
const ACCESS_REFRESH_MARGIN_MS = 10 * 60 * 1000;
/** A URL handed out by `url` is always good for at least this long. */
const URL_MIN_VALIDITY_MS = 2 * 60 * 60 * 1000;
const FETCH_CONCURRENCY = 6;
const MANIFEST_MEMORY_ENTRIES = 16;

const PERSISTENT_CACHE_PREFIX = 'fias-plugin-content-';
const PERSISTENT_CACHE_VERSION = 'v1';
const META_DB_NAME = 'fias-plugin-content-meta';
const META_STORE = 'entries';
const PERSISTENT_BUDGET_MAX_BYTES = 256 * 1024 * 1024;
const PERSISTENT_BUDGET_QUOTA_FRACTION = 0.2;
const MEMORY_BUDGET_BYTES = 32 * 1024 * 1024;
const EVICTION_LOCK = 'fias-plugin-content-cache';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type PluginContentErrorCode =
  | 'CONTENT_NOT_FOUND'
  | 'CONTENT_NOT_TEXT'
  | 'CONTENT_TOO_LARGE'
  | 'CONTENT_UNAVAILABLE'
  | 'CONTENT_THROTTLED'
  | 'CONTENT_UNAVAILABLE_IN_PREVIEW'
  | 'INVALID_INPUT';

export interface PluginContentError {
  code: PluginContentErrorCode;
  message: string;
}

export type PluginContentInput =
  | { verb: 'list'; prefix?: string }
  | { verb: 'read'; paths: string[]; as: 'text' | 'bytes' }
  | { verb: 'url'; paths: string[] };

export interface PluginContentFile {
  path: string;
  sizeBytes: number;
  mimeType: string;
}

export type PluginContentFileResult =
  | { text: string; mimeType: string }
  | { bytes: ArrayBuffer; mimeType: string }
  | { url: string; expiresAt: string }
  | { error: PluginContentError };

export type PluginContentResult =
  | { files: PluginContentFile[] }
  | { results: Record<string, PluginContentFileResult> }
  | { error: PluginContentError };

/** What the platform's content-access route returns. */
export interface PluginContentAccess {
  baseUrl: string;
  manifestHash: string;
  signedQuery: string;
  expiresAt: string;
}

export type PluginContentAccessResult =
  | { ok: true; access: PluginContentAccess }
  | { ok: false; error: PluginContentError };

/** Bytes backed by a plain ArrayBuffer — what `fetch` and Cache Storage produce. */
export type ContentBytes = Uint8Array<ArrayBuffer>;

/** A byte cache keyed by synthetic URL. Implementations must never throw from `get`. */
export interface PluginContentCache {
  get(key: string): Promise<ContentBytes | null>;
  put(key: string, bytes: ContentBytes): Promise<void>;
  delete(key: string): Promise<void>;
}

export interface PluginContentHost {
  /** The platform's credential for the arche (host-authenticated). */
  fetchAccess(archeId: string): Promise<PluginContentAccessResult>;
  /** Deployment environment, isolating cache entries ('local', 'staging', 'prod'). */
  environment: string;
  /** Injectable for tests; defaults to the global `fetch`. */
  fetch?: typeof fetch;
  /** Injectable for tests; defaults to `createPluginContentCache(environment)`. */
  cache?: PluginContentCache;
}

// ---------------------------------------------------------------------------
// Host wiring
// ---------------------------------------------------------------------------

interface HostState {
  host: PluginContentHost;
  cache: PluginContentCache;
  access: Map<string, PluginContentAccess>;
  manifests: Map<string, Map<string, PluginContentFile & { sha256: string }>>;
}

let state: HostState | null = null;

/** For tests: back to "no host configured". */
export function resetPluginContentHostForTests(): void {
  state = null;
}

/** Called once by the host at registration. Replaces any previous host. */
export function configurePluginContentHost(host: PluginContentHost): void {
  state = {
    host,
    cache: host.cache ?? createPluginContentCache(host.environment),
    access: new Map(),
    manifests: new Map(),
  };
}

// ---------------------------------------------------------------------------
// Executor
// ---------------------------------------------------------------------------

function error(code: PluginContentErrorCode, message: string): { error: PluginContentError } {
  return { error: { code, message } };
}

class ContentFetchError extends Error {
  constructor(public readonly status: number) {
    super(`content request failed (${status})`);
  }
}

/** Preview surfaces have no published pack (plan §10.6). */
function isPreviewArche(archeId: string): boolean {
  return archeId.startsWith('preview_') || archeId === 'dev_preview' || archeId.startsWith('dev_');
}

function isTextMime(mimeType: string): boolean {
  return mimeType.startsWith('text/') || mimeType.startsWith('application/json');
}

function validatePaths(value: unknown): string[] | null {
  if (!Array.isArray(value) || value.length === 0 || value.length > PLUGIN_CONTENT_MAX_PATHS) {
    return null;
  }
  if (!value.every((p) => typeof p === 'string' && p.length > 0 && p.length <= 512)) return null;
  return [...new Set(value as string[])];
}

async function sha256Hex(bytes: ContentBytes): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

function objectUrl(access: PluginContentAccess, sha256: string, channel: 'r' | 'u'): string {
  return `${access.baseUrl}/o/${sha256}?${access.signedQuery}&src=${channel}`;
}

async function getAccess(
  s: HostState,
  archeId: string,
  minValidityMs: number,
): Promise<PluginContentAccessResult> {
  const cached = s.access.get(archeId);
  if (cached && Date.parse(cached.expiresAt) - Date.now() > minValidityMs) {
    return { ok: true, access: cached };
  }
  const fresh = await s.host.fetchAccess(archeId);
  if (fresh.ok) s.access.set(archeId, fresh.access);
  else s.access.delete(archeId);
  return fresh;
}

/**
 * Fetches under the arche's credential, refreshing it once on a 403 — a
 * credential can go stale before its expiry (a rotated key, a restarted local
 * API), and the edge answers 403 for an invalid signature.
 */
async function fetchSigned(
  s: HostState,
  archeId: string,
  buildUrl: (access: PluginContentAccess) => string,
): Promise<ContentBytes> {
  const doFetch = s.host.fetch ?? fetch;
  let accessResult = await getAccess(s, archeId, ACCESS_REFRESH_MARGIN_MS);
  for (let attempt = 1; ; attempt++) {
    if (!accessResult.ok) throw accessResult.error;
    const response = await doFetch(buildUrl(accessResult.access), {
      mode: 'cors',
      credentials: 'omit',
    });
    if (response.ok) return new Uint8Array(await response.arrayBuffer());
    if (response.status === 403 && attempt === 1) {
      s.access.delete(archeId);
      accessResult = await getAccess(s, archeId, ACCESS_REFRESH_MARGIN_MS);
      continue;
    }
    throw new ContentFetchError(response.status);
  }
}

async function getManifest(
  s: HostState,
  archeId: string,
  access: PluginContentAccess,
): Promise<Map<string, PluginContentFile & { sha256: string }>> {
  const cached = s.manifests.get(access.manifestHash);
  if (cached) return cached;
  const bytes = await fetchSigned(
    s,
    archeId,
    (a) => `${a.baseUrl}/m/${access.manifestHash}.json?${a.signedQuery}&src=m`,
  );
  if ((await sha256Hex(bytes)) !== access.manifestHash) {
    throw new Error('content manifest does not match its hash');
  }
  const parsed = JSON.parse(new TextDecoder().decode(bytes)) as {
    files?: Array<{ path: string; sha256: string; sizeBytes: number; mimeType: string }>;
  };
  const byPath = new Map<string, PluginContentFile & { sha256: string }>();
  for (const f of parsed.files ?? []) {
    byPath.set(f.path, {
      path: f.path,
      sha256: f.sha256,
      sizeBytes: f.sizeBytes,
      mimeType: f.mimeType,
    });
  }
  if (s.manifests.size >= MANIFEST_MEMORY_ENTRIES) {
    s.manifests.delete(s.manifests.keys().next().value as string);
  }
  s.manifests.set(access.manifestHash, byPath);
  return byPath;
}

function cacheKey(environment: string, archeId: string, sha256: string): string {
  return `https://content-cache.fias.invalid/${encodeURIComponent(environment)}/${archeId}/${sha256}`;
}

async function getObjectBytes(
  s: HostState,
  archeId: string,
  sha256: string,
): Promise<ContentBytes> {
  const key = cacheKey(s.host.environment, archeId, sha256);
  const cached = await s.cache.get(key);
  if (cached) {
    if ((await sha256Hex(cached)) === sha256) return cached;
    await s.cache.delete(key).catch(() => undefined);
  }
  const bytes = await fetchSigned(s, archeId, (a) => objectUrl(a, sha256, 'r'));
  if ((await sha256Hex(bytes)) !== sha256) {
    throw new Error('content object does not match its hash');
  }
  // A cache failure never fails the read: serve the bytes uncached.
  await s.cache.put(key, bytes).catch(() => undefined);
  return bytes;
}

function fileError(err: unknown): PluginContentFileResult {
  if (err instanceof ContentFetchError) {
    return err.status === 429
      ? error('CONTENT_THROTTLED', 'this plugin has reached its content limit for now')
      : error('CONTENT_UNAVAILABLE', `the content could not be loaded (${err.status})`);
  }
  if (err && typeof err === 'object' && 'code' in err && 'message' in err) {
    return { error: err as PluginContentError };
  }
  return error('CONTENT_UNAVAILABLE', 'the content could not be loaded');
}

async function mapLimited<T>(items: T[], limit: number, task: (item: T) => Promise<void>) {
  const queue = [...items];
  await Promise.all(
    Array.from({ length: Math.min(limit, queue.length) }, async () => {
      for (let next = queue.shift(); next !== undefined; next = queue.shift()) await task(next);
    }),
  );
}

export async function pluginContentExecutor(
  input: PluginContentInput,
  context: ClientEntityInvocationContext,
): Promise<PluginContentResult> {
  const verb = (input as { verb?: unknown } | null)?.verb;
  if (verb !== 'list' && verb !== 'read' && verb !== 'url') {
    return error('INVALID_INPUT', 'verb must be "list", "read" or "url"');
  }
  if (isPreviewArche(context.archeId)) {
    return error(
      'CONTENT_UNAVAILABLE_IN_PREVIEW',
      'content packs are only available to published plugins, not in preview',
    );
  }
  const s = state;
  if (!s) return error('CONTENT_UNAVAILABLE', 'content is not available on this host');

  let paths: string[] = [];
  if (verb !== 'list') {
    const valid = validatePaths((input as { paths?: unknown }).paths);
    if (!valid) {
      return error(
        'INVALID_INPUT',
        `paths must be 1–${PLUGIN_CONTENT_MAX_PATHS} non-empty strings`,
      );
    }
    paths = valid;
  }
  if (verb === 'read') {
    const as = (input as { as?: unknown }).as;
    if (as !== 'text' && as !== 'bytes')
      return error('INVALID_INPUT', 'as must be "text" or "bytes"');
  }

  const accessResult = await getAccess(
    s,
    context.archeId,
    verb === 'url' ? URL_MIN_VALIDITY_MS : ACCESS_REFRESH_MARGIN_MS,
  );
  if (!accessResult.ok) return { error: accessResult.error };
  let manifest: Map<string, PluginContentFile & { sha256: string }>;
  try {
    manifest = await getManifest(s, context.archeId, accessResult.access);
  } catch (err) {
    const failed = fileError(err);
    return 'error' in failed
      ? { error: failed.error }
      : error('CONTENT_UNAVAILABLE', 'the content could not be loaded');
  }

  if (verb === 'list') {
    const prefix = (input as { prefix?: unknown }).prefix;
    if (prefix !== undefined && typeof prefix !== 'string') {
      return error('INVALID_INPUT', 'prefix must be a string');
    }
    const files = [...manifest.values()]
      .filter((f) => !prefix || f.path.startsWith(prefix))
      .map(({ path, sizeBytes, mimeType }) => ({ path, sizeBytes, mimeType }))
      .sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0));
    return { files };
  }

  const results: Record<string, PluginContentFileResult> = {};
  if (verb === 'url') {
    const access = accessResult.access;
    for (const path of paths) {
      const entry = manifest.get(path);
      results[path] = entry
        ? { url: objectUrl(access, entry.sha256, 'u'), expiresAt: access.expiresAt }
        : error('CONTENT_NOT_FOUND', `content/${path} is not in this plugin's content pack`);
    }
    return { results };
  }

  const asText = (input as { as: 'text' | 'bytes' }).as === 'text';
  let budget = PLUGIN_CONTENT_MAX_READ_BYTES;
  const toFetch: Array<{ path: string; sha256: string; mimeType: string }> = [];
  for (const path of paths) {
    const entry = manifest.get(path);
    if (!entry) {
      results[path] = error(
        'CONTENT_NOT_FOUND',
        `content/${path} is not in this plugin's content pack`,
      );
    } else if (asText && !isTextMime(entry.mimeType)) {
      results[path] = error('CONTENT_NOT_TEXT', `content/${path} is ${entry.mimeType}, not text`);
    } else if (entry.sizeBytes > budget) {
      results[path] = error(
        'CONTENT_TOO_LARGE',
        `reading content/${path} would pass the ${PLUGIN_CONTENT_MAX_READ_BYTES / (1024 * 1024)} MB per-call limit; read it on its own`,
      );
    } else {
      budget -= entry.sizeBytes;
      toFetch.push({ path, sha256: entry.sha256, mimeType: entry.mimeType });
    }
  }
  await mapLimited(toFetch, FETCH_CONCURRENCY, async ({ path, sha256, mimeType }) => {
    try {
      const bytes = await getObjectBytes(s, context.archeId, sha256);
      results[path] = asText
        ? { text: new TextDecoder().decode(bytes), mimeType }
        : { bytes: bytes.slice().buffer, mimeType };
    } catch (err) {
      results[path] = fileError(err);
    }
  });
  return { results };
}

export const pluginContentDefinition = {
  entityId: PLUGIN_CONTENT_ENTITY_ID,
  rateLimitPerMinute: 120,
  pluginPermission: 'entities:client_invoke',
  executor: pluginContentExecutor,
} as const satisfies ClientEntityDefinition<PluginContentInput, PluginContentResult>;

// ---------------------------------------------------------------------------
// Cache
// ---------------------------------------------------------------------------

/** A per-tab LRU, the fallback when persistent storage is unavailable. */
export class MemoryContentCache implements PluginContentCache {
  private readonly entries = new Map<string, ContentBytes>();
  private bytes = 0;

  constructor(private readonly budgetBytes: number = MEMORY_BUDGET_BYTES) {}

  async get(key: string): Promise<ContentBytes | null> {
    const hit = this.entries.get(key);
    if (!hit) return null;
    this.entries.delete(key);
    this.entries.set(key, hit);
    return hit;
  }

  async put(key: string, bytes: ContentBytes): Promise<void> {
    if (bytes.length > this.budgetBytes) return;
    await this.delete(key);
    this.entries.set(key, bytes);
    this.bytes += bytes.length;
    for (const [oldest, value] of this.entries) {
      if (this.bytes <= this.budgetBytes) break;
      this.entries.delete(oldest);
      this.bytes -= value.length;
    }
  }

  async delete(key: string): Promise<void> {
    const existing = this.entries.get(key);
    if (existing) {
      this.entries.delete(key);
      this.bytes -= existing.length;
    }
  }
}

interface MetaEntry {
  key: string;
  size: number;
  lastAccess: number;
}

function idbRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export interface PersistentCacheDeps {
  caches: CacheStorage;
  indexedDB: IDBFactory;
  storage?: Pick<StorageManager, 'estimate'>;
  locks?: Pick<LockManager, 'request'>;
}

/**
 * Cache Storage bytes + IndexedDB metadata, evicting least-recently-used
 * entries inside a Web Lock so tabs never evict concurrently.
 */
export class PersistentContentCache implements PluginContentCache {
  private readonly cacheName: string;
  private ready: Promise<{ cache: Cache; db: IDBDatabase }> | null = null;
  private budget: Promise<number> | null = null;

  constructor(
    environment: string,
    private readonly deps: PersistentCacheDeps,
  ) {
    this.cacheName = `${PERSISTENT_CACHE_PREFIX}${PERSISTENT_CACHE_VERSION}-${environment}`;
  }

  private open(): Promise<{ cache: Cache; db: IDBDatabase }> {
    this.ready ??= (async () => {
      // Schema migration: the version is in the name; drop any other version.
      for (const name of await this.deps.caches.keys()) {
        if (name.startsWith(PERSISTENT_CACHE_PREFIX) && name !== this.cacheName) {
          await this.deps.caches.delete(name);
        }
      }
      const request = this.deps.indexedDB.open(META_DB_NAME, 1);
      request.onupgradeneeded = () => {
        request.result.createObjectStore(META_STORE, { keyPath: 'key' });
      };
      const db = await idbRequest(request);
      return { cache: await this.deps.caches.open(this.cacheName), db };
    })();
    return this.ready;
  }

  private budgetBytes(): Promise<number> {
    this.budget ??= (async () => {
      const estimate = await this.deps.storage?.estimate().catch(() => undefined);
      const quota = estimate?.quota;
      return quota
        ? Math.min(
            PERSISTENT_BUDGET_MAX_BYTES,
            Math.floor(quota * PERSISTENT_BUDGET_QUOTA_FRACTION),
          )
        : PERSISTENT_BUDGET_MAX_BYTES;
    })();
    return this.budget;
  }

  private store(db: IDBDatabase, mode: IDBTransactionMode): IDBObjectStore {
    return db.transaction(META_STORE, mode).objectStore(META_STORE);
  }

  async get(key: string): Promise<ContentBytes | null> {
    const { cache, db } = await this.open();
    const response = await cache.match(key);
    if (!response) return null;
    const bytes = new Uint8Array(await response.arrayBuffer());
    // Best effort: a lost last-access update only makes eviction less exact.
    await idbRequest(
      this.store(db, 'readwrite').put({ key, size: bytes.length, lastAccess: Date.now() }),
    ).catch(() => undefined);
    return bytes;
  }

  async put(key: string, bytes: ContentBytes): Promise<void> {
    const { cache, db } = await this.open();
    const budget = await this.budgetBytes();
    if (bytes.length > budget) return;
    await this.evict(budget - bytes.length, 0);
    const write = async () => {
      await cache.put(key, new Response(bytes.slice()));
      await idbRequest(
        this.store(db, 'readwrite').put({ key, size: bytes.length, lastAccess: Date.now() }),
      );
    };
    try {
      await write();
    } catch (err) {
      if ((err as { name?: string })?.name !== 'QuotaExceededError') throw err;
      // Out of quota: drop the oldest quarter and try once more.
      await this.evict(budget - bytes.length, 0.25);
      await write();
    }
  }

  async delete(key: string): Promise<void> {
    const { cache, db } = await this.open();
    await cache.delete(key);
    await idbRequest(this.store(db, 'readwrite').delete(key));
  }

  /**
   * Deletes least-recently-used entries until the total fits `targetBytes`,
   * and at least `fraction` of the entries. Orphaned metadata (no cache entry)
   * goes with them.
   */
  private async evict(targetBytes: number, fraction: number): Promise<void> {
    const run = async () => {
      const { cache, db } = await this.open();
      const entries = (await idbRequest(this.store(db, 'readonly').getAll())) as MetaEntry[];
      entries.sort((a, b) => a.lastAccess - b.lastAccess);
      let total = entries.reduce((sum, e) => sum + e.size, 0);
      const minimum = Math.ceil(entries.length * fraction);
      for (let i = 0; i < entries.length; i++) {
        if (total <= targetBytes && i >= minimum) break;
        await cache.delete(entries[i].key);
        await idbRequest(this.store(db, 'readwrite').delete(entries[i].key));
        total -= entries[i].size;
      }
    };
    if (this.deps.locks) await this.deps.locks.request(EVICTION_LOCK, run);
    else await run();
  }
}

/**
 * Persistent cache first; on any failure (including construction), a per-tab
 * memory LRU for the rest of the session.
 */
export class FallbackContentCache implements PluginContentCache {
  private active: PluginContentCache;
  private readonly memory = new MemoryContentCache();

  constructor(persistent: PluginContentCache | null) {
    this.active = persistent ?? this.memory;
  }

  private async attempt<T>(operation: (cache: PluginContentCache) => Promise<T>): Promise<T> {
    try {
      return await operation(this.active);
    } catch {
      this.active = this.memory;
      return operation(this.memory);
    }
  }

  get(key: string): Promise<ContentBytes | null> {
    return this.attempt((c) => c.get(key));
  }

  put(key: string, bytes: ContentBytes): Promise<void> {
    return this.attempt((c) => c.put(key, bytes));
  }

  delete(key: string): Promise<void> {
    return this.attempt((c) => c.delete(key));
  }
}

/** The host's default cache for `environment`. */
export function createPluginContentCache(environment: string): PluginContentCache {
  const g = globalThis as {
    caches?: CacheStorage;
    indexedDB?: IDBFactory;
    navigator?: { storage?: StorageManager; locks?: LockManager };
  };
  try {
    if (g.caches && g.indexedDB) {
      return new FallbackContentCache(
        new PersistentContentCache(environment, {
          caches: g.caches,
          indexedDB: g.indexedDB,
          storage: g.navigator?.storage,
          locks: g.navigator?.locks,
        }),
      );
    }
  } catch {
    // Accessing storage can throw where site data is blocked.
  }
  return new FallbackContentCache(null);
}
